import { LanguageService } from '../service/language.service';

/** Wrapper for @type {Translatable} properties. */
export type Translated<T = any> = { value: T; translated: string; translateService?: LanguageService; valueOf: () => T };

/**
 * A property that supports translation mapping.
 *
 * MUST ALSO CALL @see TranslateProperty function at least once to set up translation mappings!
 * The simplest approach is to call the translation mapper function from the model constructor.
 */
export type Translatable<T = any> = (any extends T ? (T extends any ? never : T) : T) | Translated<T>;

/** Is true if a property is @type {Translatable} otherwise is false. */
export type IsTranslatable<Model, P extends keyof Model> = Translated extends Model[P] ? true : false;

/** Is true if a property is @type {Translated} otherwise is false. */
export function isTranslated<T>(property: Translatable<T>): property is Translated<T> {
  return typeof (property as Translated)?.translated === 'string';
}

/** Type filter that keeps only @type {Translatable} properties. */
export type TranslatablesOf<Model> = {
  [P in keyof Model as Translated extends Model[P] ? P : never]: Model[P];
};

/** Infer the underlying type of a @type {Translatable} property. */
export type OfTranslated<Model, P extends keyof TranslatablesOf<Model>> = Model[P] extends Translatable<infer T> ? T : never;

/** Type signature for a function that converts the value of a @type {Translatable} property into a translation key (to be passed to i18n translation). */
export type TranslatableValueToTranslationKeyFunc<Model, P extends keyof TranslatablesOf<Model>> = (
  parameter: OfTranslated<Model, P>
) => string;

/** Sets the @type {LanguageService} for every @type {Translatable} property of the model. */
export function SetTranslateService<Model extends {}>(model: Model, translator: LanguageService): void {
  const descMap = Object.getOwnPropertyDescriptors(model);
  for (const p in descMap) {
    if (!descMap[p].enumerable && isTranslated(model[p] as Translatable)) {
      model[p].translateService = translator;
    }
  }
}

// wrapper for logic and mappings
type TranslatableWrapper<Model, P extends keyof TranslatablesOf<Model>> = {
  data: {
    value: OfTranslated<Model, P>;
    translated: string;
    translateService?: LanguageService;
    toJSON();
    toString();
    valueOf(): OfTranslated<Model, P>;
  };
  get(): Translated<OfTranslated<Model, P>>;
  set(value: OfTranslated<Model, P>): void;
  valueToKeyFunc: TranslatableValueToTranslationKeyFunc<Model, P>;
  setter?: (value) => void;
};

export function AsTranslated<T>(property: Translatable<T>): Translated<T> {
  return isTranslated(property) ? property : null;
}

export function ValueOf<T>(property: Translatable<T>): T {
  return isTranslated(property) ? property.value : property;
}

/**
 * Defines the object mappings necessary for translation of a @type {Translatable} property.
 * @param model A model with one or more translatable properties.
 * @param property Name of a translatable property (compile-time safe).
 * @param valueToKeyFunc Function that converts the property value into a translation key. (To be translated by i18n.)
 * @param translateService Optional i18n @type {LanguageService} - only needed for 'toString' translation and string interpolations.
 */
export function TranslateProperty<Model extends {}, P extends keyof TranslatablesOf<Model>>(
  model: Model,
  property: P,
  valueToKeyFunc: TranslatableValueToTranslationKeyFunc<Model, P>,
  translateService?: LanguageService
): void {
  if (isTranslated(model[property] as Translatable)) {
    return; // already done
  }

  const propDescriptor =
    Object.getOwnPropertyDescriptor(model, property) ?? // class field -OR- getter created using Object.defineProperty
    Object.getOwnPropertyDescriptor(Object.getPrototypeOf(model), property); // getter defined using ES6 'get foo():...' syntax.

  const wrapper: TranslatableWrapper<Model, P> = {
    data: {
      value: undefined,
      translated: undefined,
      translateService: translateService,
      toJSON: () => JSON.stringify(wrapper.data.value),
      toString: () => {
        if (wrapper.data.translateService) {
          const key = wrapper.valueToKeyFunc(wrapper.data.value);
          if (key) {
            const text = wrapper.data.translateService.instant(key);
            if (text !== undefined && text !== null && text !== key) {
              return text;
            }
          }
        }
        return wrapper.data.value?.toString();
      },
      valueOf: () => wrapper.data.value,
    },
    get: () => {
      return wrapper.data;
    },
    set: undefined,
    setter: propDescriptor?.set?.bind(model),
    valueToKeyFunc: valueToKeyFunc,
  };

  if (!propDescriptor?.get || propDescriptor?.set) {
    wrapper.set = (value: any) => wrapper.setter(ValueOf(value));
  }

  if (propDescriptor?.get) {
    // it's a getter - transplant original property:
    Object.defineProperty(wrapper.data, 'value', { get: propDescriptor.get.bind(model), set: propDescriptor.set?.bind(model) });
  } else {
    // it's a field - store current value and create a setter:
    wrapper.data.value = model[property] as OfTranslated<Model, P>;
    wrapper.setter = value => {
      wrapper.data.value = value;
    };
  }

  Object.defineProperty(wrapper.data, 'translated', { get: () => wrapper.valueToKeyFunc(wrapper.data.value) || '' });
  Object.defineProperty(model, property, wrapper); // overwrite original property with translation wrapper
}
